import React from 'react';
import { RuleModel } from 'src/types/rule.model';
import { Form, useFormikContext } from 'formik';
import styles from './ruleRow.module.scss';
import Selectors from 'src/utils/selectors';
import { CategoryModel } from 'src/types/category.model';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import LiteralsService from 'src/services/literals/LiteralsService';
import Dropdown from 'src/components/formik-fields/dropdown/Dropdown';
import { trackClick } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import { SelectOption } from '@americanexpress/dls-react';
import { RuleMatcher } from 'src/types/ruleMatcher.enum';
import BasicInput from 'src/components/formik-fields/basic-input/BasicInput';
import CategorySelectDetailed from 'src/components/atomic/atoms/categorySelectDetailed/CategorySelectDetailed';

interface RuleProps {
    rule: Partial<RuleModel>;
    pathname: string;
    button: JSX.Element;
    categories?: CategoryModel[];
    ariaLabel?: string;
    matcherId?: string;
    nameId?: string;
    categorySelectId?: string;
    formStyle?: string;
    icon?: JSX.Element;
}

export default function RuleRow({
    rule,
    pathname,
    categories,
    button,
    ariaLabel,
    matcherId,
    nameId,
    categorySelectId,
    formStyle,
    icon
}: RuleProps): JSX.Element {
    const { setFieldValue } = useFormikContext<RuleModel>();

    return (
        <Form
            className={`${styles.ruleForm} ${formStyle}`}
            aria-label={ariaLabel}
            data-cy={Selectors.Transactions.RULE_ROW}
        >
            <div className={styles.ruleGroup}>
                {icon}
                <Typography className={styles.ruleText} variant={TypographyVariants.BODY}>
                    {LiteralsService.getLiteral('RuleRow.if')}
                </Typography>
                <Dropdown
                    id={matcherId || 'ruleMatcher'}
                    label={LiteralsService.getLiteral('RuleRow.matchCriteriaOfRule')}
                    className={styles.ruleDropdown}
                    onChange={(optionName: string) =>
                        trackClick(pathname, `${adobeTags.Dropdown.Rule.Contains}>${optionName.toLowerCase()}`)
                    }
                    hideLabel
                    allowEmptyOption={false}
                    data-cy={Selectors.Transactions.RULE_TYPE_DROPDOWN}
                >
                    <SelectOption key={RuleMatcher.CONTAINS} value={RuleMatcher.CONTAINS}>
                        {LiteralsService.getLiteral('RuleRow.containsOption')}
                    </SelectOption>
                    <SelectOption key={RuleMatcher.STARTS_WITH} value={RuleMatcher.STARTS_WITH}>
                        {LiteralsService.getLiteral('RuleRow.startsWithOption')}
                    </SelectOption>
                    <SelectOption key={RuleMatcher.ENDS_WITH} value={RuleMatcher.ENDS_WITH}>
                        {LiteralsService.getLiteral('RuleRow.endsWithOption')}
                    </SelectOption>
                    <SelectOption key={RuleMatcher.EQUALS} value={RuleMatcher.EQUALS}>
                        {LiteralsService.getLiteral('RuleRow.equalsOption')}
                    </SelectOption>
                </Dropdown>
            </div>
            <div className={styles.ruleGroup}>
                <Typography className={styles.ruleText} variant={TypographyVariants.BODY}>
                    {LiteralsService.getLiteral('RuleRow.this')}
                </Typography>
                <BasicInput
                    id={nameId || 'ruleTransactionName'}
                    className={styles.ruleInput}
                    label={LiteralsService.getLiteral('RuleRow.textToMatchOfRule')}
                    placeholder={LiteralsService.getLiteral('RuleRow.namePlaceholder')}
                    hideLabel
                />
            </div>
            <div className={styles.ruleGroup}>
                <Typography className={styles.ruleText} variant={TypographyVariants.BODY}>
                    {LiteralsService.getLiteral('RuleRow.moveTo')}
                </Typography>
                <CategorySelectDetailed
                    id={categorySelectId || 'ruleGroup'}
                    categories={categories}
                    pathname={pathname}
                    className={styles.ruleInput}
                    value={rule}
                    tagName={adobeTags.Dropdown.Rule.Category}
                    placeholder={LiteralsService.getLiteral('RuleRow.selectCategory')}
                    label={LiteralsService.getLiteral('RuleRow.categoryOfRule')}
                    hideLabel
                    onCloseCustomSubcategoryModal={async (category: CategoryModel) =>
                        setFieldValue('groupUid', category.id)
                    }
                />
            </div>
            {button}
        </Form>
    );
}
